import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

const TYPE_LABELS = {
  cr_rdv: 'CR RDV',
  brief: 'Brief',
  bilan: 'Bilan',
  contrat: 'Contrat',
};

const POPOVER_MAX_HEIGHT = 280;
const POPOVER_GAP = 6;
const VIEWPORT_MARGIN = 12;

function formatDate(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('fr-FR');
}

function computePopoverLayout(chipRect) {
  const spaceBelow = window.innerHeight - chipRect.bottom - VIEWPORT_MARGIN;
  const spaceAbove = chipRect.top - VIEWPORT_MARGIN;
  const openBelow = spaceBelow >= 140 || spaceBelow >= spaceAbove;
  const available = openBelow
    ? Math.min(POPOVER_MAX_HEIGHT, spaceBelow - POPOVER_GAP)
    : Math.min(POPOVER_MAX_HEIGHT, spaceAbove - POPOVER_GAP);

  const maxHeight = Math.max(120, available);
  const minWidth = Math.min(380, Math.max(chipRect.width, 260));
  const left = Math.min(
    chipRect.left,
    window.innerWidth - minWidth - VIEWPORT_MARGIN,
  );

  if (openBelow) {
    return {
      placement: 'below',
      style: {
        top: chipRect.bottom + POPOVER_GAP,
        left: Math.max(VIEWPORT_MARGIN, left),
        minWidth,
        maxWidth: Math.min(380, window.innerWidth - VIEWPORT_MARGIN * 2),
        maxHeight,
      },
    };
  }

  return {
    placement: 'above',
    style: {
      bottom: window.innerHeight - chipRect.top + POPOVER_GAP,
      left: Math.max(VIEWPORT_MARGIN, left),
      minWidth,
      maxWidth: Math.min(380, window.innerWidth - VIEWPORT_MARGIN * 2),
      maxHeight,
    },
  };
}

export default function AssistantSources({ sources = [] }) {
  const [open, setOpen] = useState(false);
  const [popoverLayout, setPopoverLayout] = useState(null);
  const chipRef = useRef(null);
  const popoverRef = useRef(null);

  const uniqueSources = useMemo(() => {
    const map = new Map();
    sources.forEach((source) => {
      const key = source.lien_drive || source.nom;
      if (key && !map.has(key)) {
        map.set(key, source);
      }
    });
    return Array.from(map.values());
  }, [sources]);

  const updatePopoverLayout = useCallback(() => {
    const chip = chipRef.current;
    if (!chip) {
      return;
    }
    setPopoverLayout(computePopoverLayout(chip.getBoundingClientRect()));
  }, []);

  useEffect(() => {
    if (!open) {
      setPopoverLayout(null);
      return undefined;
    }

    updatePopoverLayout();

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        chipRef.current?.contains(target)
        || popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    const handleReposition = () => {
      updatePopoverLayout();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePopoverLayout]);

  if (uniqueSources.length === 0) {
    return null;
  }

  const countLabel = uniqueSources.length > 1
    ? `${uniqueSources.length} sources`
    : '1 source';

  const popover = open && popoverLayout
    ? createPortal(
      <div
        ref={popoverRef}
        className={`assistant-sources__popover assistant-sources__popover--${popoverLayout.placement}`}
        role="dialog"
        aria-label="Sources utilisées"
        style={{
          position: 'fixed',
          zIndex: 1080,
          ...popoverLayout.style,
        }}
        onWheel={(event) => event.stopPropagation()}
      >
        <ul className="assistant-sources__list list-unstyled mb-0">
          {uniqueSources.map((source) => {
            const typeLabel = TYPE_LABELS[source.type_document] || source.type_document;
            const dateLabel = formatDate(source.date_document);

            return (
              <li key={source.lien_drive || source.nom} className="assistant-sources__item">
                <a
                  href={source.lien_drive}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="assistant-sources__link"
                >
                  <span className="assistant-sources__name">{source.nom || 'Document'}</span>
                  <Icon icon="mdi:open-in-new" className="assistant-sources__external" />
                </a>
                {(typeLabel || dateLabel) && (
                  <div className="assistant-sources__meta">
                    {typeLabel && (
                      <span className="assistant-sources__badge">{typeLabel}</span>
                    )}
                    {dateLabel && (
                      <span className="assistant-sources__date">{dateLabel}</span>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>,
      document.body,
    )
    : null;

  return (
    <div className="assistant-sources">
      <button
        ref={chipRef}
        type="button"
        className="assistant-sources__chip"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Voir les ${countLabel}`}
      >
        <Icon icon="mdi:file-document-multiple-outline" className="assistant-sources__chip-icon" />
        <span>{countLabel}</span>
        <Icon
          icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          className="assistant-sources__chip-caret"
        />
      </button>
      {popover}
    </div>
  );
}
